import React, { Component } from 'react';
import T from 'prop-types';
// recompose
import { compose } from 'recompose';
import withDataTableController from 'components/DataTable/DataTableController/DataTableController';
// redux
import { connect } from 'react-redux';
import { fetchData } from 'reducers/dataTable';
// components
import DropdownButton from 'components/DropdownButton/DropdownButton';
import SearchBox from 'components/SearchBox';
import TableFilter from 'components/TableFilter';
import DataTable from 'components/DataTable/DataTable';
import CustomColumn from 'components/DataTable/Columns/CustomColumn';
import Preloader from 'components/Preloader';
import { GroupStatusMetaFarms } from 'components/StatusBadge/StatusBadge';
import Panel from 'components/Panel';
import TruncateHint from 'components/TruncateHint';
import NothingBox from 'components/NothingBox';
import ClosedIntegration from '../../ClosedIntegration/ClosedIntegration';
// utils
import { fetchFromAPI } from 'utils/api';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import download from 'downloadjs';
import { isTablet } from 'react-device-detect';

const tableFilters = [
  { label: <FormattedMessage id="general.allGroups" />, value: '' },
  { label: <FormattedMessage id="general.errors" />, value: 'errors' },
];

const urlDownload = '/push_results/download?direction=export&api=amino';

class PushResults extends Component {
  state = { isLoading: false };

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    this.setState({ isLoading: true });
    const { reqParams, fetchData } = this.props;
    fetchData({ ...reqParams, sort: 'status asc' })
      .catch(toastResponseErrors)
      .finally(() => this.setState({ isLoading: false }));
  };

  onFilterChange = (filter) => {
    this.setState({ isLoading: true });
    const { reqParams, fetchData } = this.props;
    fetchData({ ...reqParams, status: filter, sort: 'status asc' })
      .catch(toastResponseErrors)
      .finally(() => this.setState({ isLoading: false }));
  };

  onSearchChange = (searchName) => {
    this.setState({ isLoading: true });
    const { reqParams, fetchData } = this.props;
    fetchData({ ...reqParams, search: searchName, sort: 'status asc' })
      .catch(toastResponseErrors)
      .finally(() => this.setState({ isLoading: false }));
  };

  downloadCSV = (path, fileName) => (e) => {
    e.stopPropagation();
    return fetchFromAPI(path, { blob: true })
      .then((blob) => download(blob, fileName, 'text/csv'))
      .catch(toastResponseErrors);
  };

  downloadAll = () => {
    const ts = moment().format('MM-DD-YYYY-h-mm-a');
    fetchFromAPI(urlDownload, { blob: true })
      .then((blob) => download(blob, `EveryPig_Amino_Push_Results_${ts}.csv`, 'text/csv'))
      .catch(toastResponseErrors);
  };

  getUrlDownload = (success) => `${urlDownload}&success=${success ? 'true' : 'false'}`;

  getNameDownload = (success) => {
    const ts = moment().format('MM-DD-YYYY-h-mm-a');
    return `EveryPig_Amino_Push_Results_${success ? 'Successes' : 'Errors'}_${ts}.csv`;
  };

  renderGroupColumn = ({ data: { pig_group_name } }) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.groupId" />}>
      <TruncateHint name={pig_group_name || 'N/A'} className="groupIdName" />
    </CustomColumn>
  );

  renderFarmColumn = ({ data: { farm_name } }) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.farmName" />}>
      <TruncateHint name={farm_name || 'N/A'} className="groupIdName" />
    </CustomColumn>
  );

  renderNumberColumn = (key, label) => (row) => (
    <CustomColumn textRight label={label}>
      {row.data?.payload?.[key] ?? 0}
    </CustomColumn>
  );

  renderStatusColumn = ({ success }) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />} noBottomBorder>
      <GroupStatusMetaFarms status={success ? 'success' : 'error'} />
    </CustomColumn>
  );

  render() {
    const {
      reqParams: { page, per_page, search, sort, status },
      meta: { total, stats },
      amino,
      active,
      onSortChange,
      onPageChange,
      onPerPageChange,
    } = this.props;
    const { isLoading } = this.state;

    if (isLoading) return <Preloader isActive />;
    if (!active) return <ClosedIntegration link="amino" />;

    const datePush = moment(Date.now()).format('MM/DD/YY HH:MM:ss GTM ZZ');
    const columns = [
      {
        label: <FormattedMessage id="component.dataTable.headers.groupId" />,
        flex: '1 1 130px',
        renderer: this.renderGroupColumn,
        sortKey: 'pig_group_name',
      },
      {
        label: <FormattedMessage id="component.dataTable.headers.farmName" />,
        flex: '1 1 130px',
        renderer: this.renderFarmColumn,
        sortKey: 'farm_name',
      },
      {
        label: <FormattedMessage id="general.deaths.acute" />,
        flex: '1 1 80px',
        renderer: this.renderNumberColumn('acute', <FormattedMessage id="general.deaths.acute" />),
        sortKey: 'acute',
        textRight: true,
      },
      {
        label: <FormattedMessage id="general.deaths.chronic" />,
        flex: '1 1 80px',
        renderer: this.renderNumberColumn('chronic', <FormattedMessage id="general.deaths.chronic" />),
        sortKey: 'chronic',
        textRight: true,
      },
      {
        label: <FormattedMessage id="general.deaths.euthanasia" />,
        flex: '1 1 90px',
        renderer: this.renderNumberColumn('euthanized', <FormattedMessage id="general.deaths.euthanasia" />),
        sortKey: 'euthanized',
        textRight: true,
      },
      {
        label: <FormattedMessage id="general.deaths.treated" />,
        flex: '1 1 80px',
        renderer: this.renderNumberColumn('treated', <FormattedMessage id="general.deaths.treated" />),
        sortKey: 'treated',
        textRight: true,
      },
      {
        label: <FormattedMessage id="component.dataTable.headers.pushResult" />,
        flex: '1 1 110px',
        renderer: this.renderStatusColumn,
        sortKey: 'status',
      },
    ];

    return (
      <div className="small-12 column">
        <div className="BarnsheetsGroups">
          <Panel className="mv-20">
            <Panel.Heading
              renderTitle={() => (
                <h2 className="lighter show-for-large">
                  <FormattedMessage id="general.pageTitle.results" /> {datePush}
                </h2>
              )}
            >
              <SearchBox initialValue={search} onChange={this.onSearchChange} />
            </Panel.Heading>
            <Panel.Body noPadding>
              <Preloader isActive={isLoading} />
              <TableFilter
                filters={tableFilters}
                onFilterChange={this.onFilterChange}
                activeFilter={status}
                stats={stats}
                className="mh-10"
              >
                <div className="containerButtons">
                  <DropdownButton
                    idKey="amino-dropdown"
                    ignoreScroll
                    label={<FormattedMessage id="general.button.download" />}
                    buttonType="compact"
                    onClick={this.downloadAll}
                    dropDownData={[
                      {
                        label: <FormattedMessage id="general.download.successes" />,
                        onClick: this.downloadCSV(this.getUrlDownload(true), this.getNameDownload(true)),
                      },
                      {
                        label: <FormattedMessage id="general.download.errors" />,
                        onClick: this.downloadCSV(this.getUrlDownload(false), this.getNameDownload(false)),
                      },
                    ]}
                    customClass="show-for-large"
                    wide
                  />
                </div>
              </TableFilter>
              {!amino.length ? (
                <NothingBox display={!amino.length} itemsName="amino" isLoading={isLoading} filter="pushResults" />
              ) : (
                <DataTable
                  data={amino}
                  getRowClass={({ success }) => ({ pink: !success })}
                  columns={columns}
                  sort={sort}
                  onSortChange={onSortChange}
                  paginationProps={{
                    onPageChange, onPerPageChange, totalItems: total, currentPage: page, perPage: per_page
                  }}
                  isExpandable={isTablet}
                  scrollable
                  tableKey="amino"
                />
              )}
            </Panel.Body>
          </Panel>
        </div>
      </div>
    );
  }
}

PushResults.propTypes = {
  reqParams: T.object.isRequired,
  meta: T.object.isRequired,
  fetchData: T.func.isRequired,
  onSortChange: T.func.isRequired,
  onPageChange: T.func.isRequired,
  onPerPageChange: T.func.isRequired,
  amino: T.array,
  active: T.bool,
};

const enhance = compose(
  connect(
    (state) => ({
      meta: state.dataTable.amino.meta,
      reqParams: state.dataTable.amino.params,
      amino: state.dataTable.amino.resources,
      active: state.dataTable.amino.meta.active,
    }),
    (dispatch) => ({
      fetchData: (query) => dispatch(fetchData('/push_results?api=amino', 'amino', query)),
    })
  ),
  withDataTableController('fetchData', 'reqParams')
);

export default enhance(PushResults);
